
import { TrainingFormValues, TrainingSession, CompetitionMatch } from '../../types';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/contexts/unified-permissions';
import { parseStripeCount, formatStripeCount } from '../../utils/stripeCountUtils';

// Define the database response type to match actual Supabase schema
interface DatabaseTrainingSession {
  id: string;
  user_id: string;
  // New schema fields (aligned with form)
  title: string;
  duration: number;
  location: string;
  youtube_url: string;
  session_type: string;
  professor: string;
  learned: string;
  improvements: string;
  successes: string;
  competition_finish: string;
  competition_matches: CompetitionMatch[];
  // Common fields
  date: string;
  notes: string;
  belt_rank: string;
  stripe_count: number;
  techniques_practiced: string[];
  created_at: string;
  updated_at: string;
}

export const useSessionOperations = () => {
  const { user } = usePermissions();

  const addSession = async (session: TrainingFormValues): Promise<TrainingSession | null> => {
    if (!user) throw new Error('User not authenticated');

    // Prepare data with correct field names for database
    const safeDuration = session.duration != null ? Number(session.duration) : 60;
    const safeProfessor = session.professor || '';
    const safeLocation = session.location || '';
    const safeNotes = session.notes || '';
    const safeLearned = session.learned || '';
    const safeImprovements = session.improvements || '';
    const safeSuccesses = session.successes || '';
    const safeYoutubeUrl = session.youtube_url || '';
    const safeBeltRank = session.belt_rank || 'white';
    const safeStripeCount = parseStripeCount(session.stripe_count);

    const insertData = {
      user_id: user.id,
      title: session.sessionType || 'Gi',
      session_type: session.sessionType || 'Gi',
      date: session.date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
      duration: safeDuration,
      location: safeLocation,
      professor: safeProfessor,
      techniques_practiced: Array.isArray(session.techniques) ? session.techniques :
                           (session.techniques ? [session.techniques] : []),
      notes: safeNotes,
      learned: safeLearned,
      improvements: safeImprovements,
      successes: safeSuccesses,
      youtube_url: safeYoutubeUrl,
      belt_rank: safeBeltRank,
      stripe_count: safeStripeCount,
      competition_finish: session.competition_finish || '',
      competition_matches: session.competition_matches || [],
    };

    const { data, error } = await supabase
      .from('training_sessions')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('❌ Supabase insert error:', error);
      throw error;
    }

    // Convert database response to TrainingSession type
    const dbSession = data as DatabaseTrainingSession;

    return {
      id: dbSession.id,
      userId: dbSession.user_id,
      title: dbSession.title,
      date: dbSession.date,
      duration: dbSession.duration,
      professor: dbSession.professor,
      techniques: dbSession.techniques_practiced || [],
      location: dbSession.location,
      notes: dbSession.notes,
      learned: dbSession.learned,
      improvements: dbSession.improvements,
      successes: dbSession.successes,
      youtubeUrl: dbSession.youtube_url,
      beltRank: dbSession.belt_rank,
      stripeCount: formatStripeCount(dbSession.stripe_count),
      createdAt: dbSession.created_at,
      updatedAt: dbSession.updated_at,
      competitionTotalMatches: dbSession.competition_matches ? dbSession.competition_matches.length : 0,
      competitionWeightClasses: '',
      competitionTotalWins: dbSession.competition_matches ? dbSession.competition_matches.filter((m: CompetitionMatch) => m.result === 'win').length : 0,
      competitionFinish: dbSession.competition_finish,
      competitionMatches: dbSession.competition_matches || []
    };
  };

  const editSession = async (id: string, updates: TrainingFormValues): Promise<TrainingSession | null> => {
    if (!user) throw new Error('User not authenticated');

    // Prepare data with correct field names for database
    const safeDuration = updates.duration != null ? Number(updates.duration) : 60;
    const safeProfessor = updates.professor || '';
    const safeLocation = updates.location || '';
    const safeNotes = updates.notes || '';
    const safeLearned = updates.learned || '';
    const safeImprovements = updates.improvements || '';
    const safeSuccesses = updates.successes || '';
    const safeYoutubeUrl = updates.youtube_url || '';
    const safeBeltRank = updates.belt_rank || 'white';
    const safeStripeCount = parseStripeCount(updates.stripe_count);

    const { data, error } = await supabase
      .from('training_sessions')
      .update({
        title: updates.sessionType || 'Gi',
        session_type: updates.sessionType || 'Gi',
        date: updates.date?.toISOString().split('T')[0],
        duration: safeDuration,
        location: safeLocation,
        professor: safeProfessor,
        techniques_practiced: Array.isArray(updates.techniques) ? updates.techniques :
                             (updates.techniques ? [updates.techniques] : []),
        notes: safeNotes,
        learned: safeLearned,
        improvements: safeImprovements,
        successes: safeSuccesses,
        youtube_url: safeYoutubeUrl,
        belt_rank: safeBeltRank,
        stripe_count: safeStripeCount,
        // Competition specific fields
        competition_finish: updates.competition_finish || '',
        competition_matches: updates.competition_matches || [],
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;

    // Convert database response to TrainingSession type
    const dbSession = data as DatabaseTrainingSession;

    return {
      id: dbSession.id,
      userId: dbSession.user_id,
      title: dbSession.title,
      date: dbSession.date,
      duration: dbSession.duration,
      professor: dbSession.professor,
      techniques: dbSession.techniques_practiced || [],
      location: dbSession.location,
      notes: dbSession.notes,
      learned: dbSession.learned,
      improvements: dbSession.improvements,
      successes: dbSession.successes,
      youtubeUrl: dbSession.youtube_url,
      beltRank: dbSession.belt_rank,
      stripeCount: formatStripeCount(dbSession.stripe_count),
      createdAt: dbSession.created_at,
      updatedAt: dbSession.updated_at,
      competitionTotalMatches: dbSession.competition_matches ? dbSession.competition_matches.length : 0,
      competitionWeightClasses: '',
      competitionTotalWins: dbSession.competition_matches ? dbSession.competition_matches.filter((m: CompetitionMatch) => m.result === 'win').length : 0,
      competitionFinish: dbSession.competition_finish,
      competitionMatches: dbSession.competition_matches || []
    };
  };

  const deleteSession = async (id: string): Promise<boolean> => {
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('training_sessions')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
    return true;
  };

  return {
    addSession,
    editSession,
    deleteSession,
  };
};
